import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Divider,
    Grid,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Skeleton,
    Chip,
    Button,
    Alert,
    Tooltip,
    Tabs,
    Tab
} from '@mui/material';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonIcon from '@mui/icons-material/Person';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintIcon from '@mui/icons-material/Print';
import UndoIcon from '@mui/icons-material/Undo';
import { generateReceiptPdf, generateFolioInvoicePdf } from '../../../utils/exportUtils';
import { formatCurrency, useCurrency } from '../../../utils/currency';
import { isUaeProperty, calculateDubaiHospitalityTax, calculateDubaiFolioBreakdown } from '../../../utils/dubaiHospitalityTax';
import { FinanceService } from '../../../api/services/financeService';
import { SettingsService } from '../../../api/services/settingsService';
import AddFolioChargeModal from './AddFolioChargeModal';
import EditFolioChargeModal from './EditFolioChargeModal';
import VoidFolioChargeModal from './VoidFolioChargeModal';
import AddFolioPaymentModal from './AddFolioPaymentModal';
import ProcessFolioRefundModal from './ProcessFolioRefundModal';
import { standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function GuestFolioDrawer({ open, onClose, reservationId, reservation, tenantId: propTenantId }) {
    const authData = (() => {
        try {
            return JSON.parse(localStorage.getItem('adminAuth') || '{}');
        } catch (_) {
            return {};
        }
    })();
    const tenantId = propTenantId || authData.tenantId || '1';
    const { tenants } = useSelector((state) => state.auth || { tenants: [] });
    const tenant = tenants.find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));
    const hotelStatus = tenant?.status || 'Active';
    const isArchived = hotelStatus === 'Archived';
    const { currency } = useCurrency();
    const isUae = currency === 'AED' || (tenant?.currency === 'AED') || (reservation?.currency === 'AED') || isUaeProperty(tenant) || isUaeProperty(reservation?.hotel);

    const [folio, setFolio] = useState(null);
    const [foliosList, setFoliosList] = useState([]);
    const [activeFolioIndex, setActiveFolioIndex] = useState(0);
    const [billingMode, setBillingMode] = useState('master');
    const [loading, setLoading] = useState(false);
    const [chargeModalOpen, setChargeModalOpen] = useState(false);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [refundModalOpen, setRefundModalOpen] = useState(false);
    const [editModalState, setEditModalState] = useState({ open: false, charge: null });
    const [voidModalState, setVoidModalState] = useState({ open: false, charge: null });
    const [activeTaxes, setActiveTaxes] = useState([]);

    useEffect(() => {
        if (!open || !tenantId) return;
        const fetchTaxes = async () => {
            try {
                const result = await SettingsService.getTaxes(tenantId, { noPagination: true });
                const list = Array.isArray(result) ? result : (result?.data || result?.taxes || []);
                setActiveTaxes(list.filter(t => t.status === 'Active'));
            } catch (err) {
                console.error("Failed to load taxes in GuestFolioDrawer:", err);
            }
        };
        fetchTaxes();
    }, [open, tenantId]);

    const handleTabChange = (event, newValue) => {
        setActiveFolioIndex(newValue);
        if (foliosList[newValue]) {
            setFolio(foliosList[newValue]);
        }
    };

    useEffect(() => {
        if (open && reservationId) {
            setActiveFolioIndex(0);
            fetchFolioDetails(0);
        } else {
            setFolio(null);
            setFoliosList([]);
        }
    }, [open, reservationId]);

    const fetchFolioDetails = async (forceIndex = null) => {
        setLoading(true);
        try {
            const data = await FinanceService.getGuestFolioLedger(reservationId);
            setFoliosList(data.folios);
            if (data.folios && data.folios.length > 0) {
                let activeIdx = forceIndex !== null ? forceIndex : activeFolioIndex;
                if (activeIdx >= data.folios.length) activeIdx = 0;
                setActiveFolioIndex(activeIdx);
                setFolio(data.folios[activeIdx]);
            } else {
                setFolio(data.primaryFolio);
                setActiveFolioIndex(0);
            }
            setBillingMode(data.billingMode);
        } catch (error) {
            console.error('Failed to fetch guest folio ledger:', error);
            onClose(); // Automatically handled via interceptor, close drawer gracefully
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return 'N/A';
        return new Date(isoString).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const formatDateTimeParts = (isoString) => {
        if (!isoString) return { date: 'N/A', time: '' };
        try {
            const d = new Date(isoString);
            if (isNaN(d.getTime())) return { date: String(isoString), time: '' };
            const dateStr = d.toLocaleDateString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric'
            });
            const timeStr = d.toLocaleTimeString('en-US', {
                hour: '2-digit', minute: '2-digit'
            });
            return { date: dateStr, time: timeStr };
        } catch (_) {
            return { date: 'N/A', time: '' };
        }
    };

    const renderSkeletons = () => (
        <Box sx={{ p: 3 }}>
            <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 2, mb: 3 }} />
            <Skeleton variant="rectangular" width="100%" height={250} sx={{ borderRadius: 2, mb: 3 }} />
            <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 2 }} />
        </Box>
    );

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: '100%', md: 880, lg: 960 },
                    bgcolor: '#f8fafc',
                    borderTopLeftRadius: 24,
                    borderBottomLeftRadius: 24,
                    boxShadow: '-12px 0 48px rgba(15, 23, 42, 0.12)',
                    borderLeft: '1px solid rgba(226, 232, 240, 0.9)'
                }
            }}
        >
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2.25,
                bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(20px)',
                borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2.5,
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)'
                    }}>
                        <ReceiptLongIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight="800" sx={{ color: '#0f172a', fontSize: '1.15rem', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                            Guest Folio Ledger
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.78rem' }}>
                            Live on-property billing, itemized charges & payment settlements
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        width: 34,
                        height: 34,
                        bgcolor: '#f1f5f9',
                        color: '#64748b',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#e2e8f0', color: '#0f172a', transform: 'scale(1.05)' },
                        transition: 'all 0.15s ease'
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {loading ? renderSkeletons() : folio ? (
                <Box sx={{ p: 3 }}>

                    {/* Property Archived Alert */}
                    {isArchived && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 3, fontWeight: 500, boxShadow: '0 2px 10px rgba(239, 68, 68, 0.1)' }}>
                            <b>Immutable State:</b> This property is Archived. Guest folios are in a read-only state. No charges or payments can be added, edited, or voided.
                        </Alert>
                    )}

                    {/* Room / Folio Segment Tabs */}
                    {foliosList && foliosList.length > 1 && (
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{
                                bgcolor: '#e2e8f0',
                                p: 0.5,
                                borderRadius: 3,
                                display: 'inline-flex',
                                maxWidth: '100%',
                                overflowX: 'auto'
                            }}>
                                <Tabs
                                    value={activeFolioIndex}
                                    onChange={handleTabChange}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    TabIndicatorProps={{ style: { display: 'none' } }}
                                    sx={{
                                        minHeight: 36,
                                        '& .MuiTab-root': {
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            minHeight: 36,
                                            py: 0.75,
                                            px: 2.5,
                                            borderRadius: 2.5,
                                            color: '#64748b',
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&.Mui-selected': {
                                                color: '#1e293b',
                                                bgcolor: '#ffffff',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                            }
                                        }
                                    }}
                                >
                                    {foliosList.map((f, idx) => {
                                        const roomMatch = (reservation?.rooms || []).find(r =>
                                            (f.reservationRoomId && (r.id === f.reservationRoomId || r.roomId === f.reservationRoomId || r.reservationRoomId === f.reservationRoomId))
                                        );
                                        const roomNum = f.reservationRoom?.room?.roomNumber || f.reservationRoom?.roomNumber || roomMatch?.room?.roomNumber || roomMatch?.roomNumber || (reservation?.rooms?.[idx]?.room?.roomNumber || reservation?.rooms?.[idx]?.roomNumber);
                                        const label = roomNum ? `Room ${roomNum}` : (f.id ? `Folio #${f.id.slice(0, 6).toUpperCase()}` : `Folio ${idx + 1}`);
                                        return <Tab key={f.id || idx} label={label} />;
                                    })}
                                </Tabs>
                            </Box>
                        </Box>
                    )}

                    {/* Folio Metadata & Overview Card */}
                    <Paper sx={{
                        p: 3, borderRadius: 4, mb: 3, elevation: 0,
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.04)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            position: 'absolute', top: 0, left: 0, right: 0, height: 3.5,
                            background: 'linear-gradient(90deg, #3b82f6 0%, #6366f1 50%, #8b5cf6 100%)'
                        }} />

                        <Grid container spacing={2.5} alignItems="center">
                            {/* Folio Reference ID */}
                            <Grid item xs={12} sm={4}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                    Folio Reference
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Typography variant="body1" sx={{ color: '#0f172a', fontWeight: 700, fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                        {folio.id ? `${folio.id.substring(0, 8)}...${folio.id.substring(folio.id.length - 4)}` : (folio.folioId || reservationId?.substring(0, 8).toUpperCase() || 'N/A')}
                                    </Typography>
                                    <Tooltip title={folio.id || folio.folioId || reservationId}>
                                        <Chip
                                            label={billingMode === 'individual' ? 'Room Folio' : 'Master Bill'}
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.65rem',
                                                fontWeight: 700,
                                                bgcolor: '#eff6ff',
                                                color: '#2563eb',
                                                border: '1px solid #dbeafe'
                                            }}
                                        />
                                    </Tooltip>
                                </Box>
                            </Grid>

                            {/* Folio Status */}
                            <Grid item xs={6} sm={4}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                    Settlement Status
                                </Typography>
                                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                                    {(() => {
                                        const isSettled = folio.status === 'Closed' || folio.status === 'Settled';
                                        return (
                                            <Box sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                bgcolor: isSettled ? '#ecfdf5' : '#eff6ff',
                                                color: isSettled ? '#047857' : '#1d4ed8',
                                                border: `1px solid ${isSettled ? '#a7f3d0' : '#bfdbfe'}`,
                                                borderRadius: '9999px',
                                                px: 1.25,
                                                py: 0.35,
                                                fontWeight: 700,
                                                fontSize: '0.75rem'
                                            }}>
                                                <Box sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    bgcolor: isSettled ? '#10b981' : '#3b82f6'
                                                }} />
                                                {folio.status || 'Open'}
                                            </Box>
                                        );
                                    })()}
                                </Box>
                            </Grid>

                            {/* Stay Dates */}
                            <Grid item xs={6} sm={4}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarMonthIcon sx={{ fontSize: 14, color: '#3b82f6' }} /> Stay Period
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600, mt: 0.5, fontSize: '0.85rem' }}>
                                    {formatDate(reservation?.checkIn)} — {formatDate(reservation?.checkOut)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Line-Items Ledger Table */}
                    <Paper sx={{
                        borderRadius: 4, mb: 3, elevation: 0, overflow: 'hidden',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.05)'
                    }}>
                        <Box sx={{
                            px: 3, py: 2.25, display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 2,
                            borderBottom: '1px solid #f1f5f9', bgcolor: '#ffffff'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Typography variant="subtitle1" fontWeight="800" sx={{ color: '#0f172a', fontSize: '1.05rem', letterSpacing: '-0.01em' }}>
                                    Transaction Ledger
                                </Typography>
                                <Chip
                                    label={`${(folio.items || folio.transactions || []).length} Records`}
                                    size="small"
                                    sx={{
                                        height: 24,
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        bgcolor: '#f1f5f9',
                                        color: '#475569',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        px: 0.5
                                    }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'nowrap' }}>
                                <Tooltip title="Download official tax invoice in PDF format">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                                        onClick={() => generateFolioInvoicePdf(folio, reservation, tenant, activeTaxes)}
                                        sx={standardSecondaryButtonSx}
                                    >
                                        Tax Invoice
                                    </Button>
                                </Tooltip>
                                {(() => {
                                    const isClosedStatus = ['Cancelled', 'No-show', 'Checked-out'].includes(reservation?.status);

                                    return (
                                        <>
                                            <Tooltip title={isArchived ? "Folio is read-only (Property Archived)" : ""}>
                                                <span>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<UndoIcon sx={{ fontSize: 17 }} />}
                                                        onClick={() => setRefundModalOpen(true)}
                                                        disabled={isArchived}
                                                        sx={{
                                                            height: 38,
                                                            borderRadius: 2.5,
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            fontSize: '0.825rem',
                                                            px: 2,
                                                            whiteSpace: 'nowrap',
                                                            flexShrink: 0,
                                                            color: '#b45309',
                                                            borderColor: '#fde68a',
                                                            bgcolor: '#fffbeb',
                                                            boxShadow: '0 1px 2px rgba(217, 119, 6, 0.04)',
                                                            '&:hover': {
                                                                bgcolor: '#fef3c7',
                                                                borderColor: '#fcd34d',
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: '0 3px 8px rgba(217, 119, 6, 0.12)'
                                                            },
                                                            '&.Mui-disabled': {
                                                                bgcolor: '#f8fafc',
                                                                borderColor: '#e2e8f0',
                                                                color: '#94a3b8'
                                                            },
                                                            transition: 'all 0.15s ease'
                                                        }}
                                                    >
                                                        Issue Refund
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={isArchived ? "Folio is read-only (Property Archived)" : (isClosedStatus ? `Cannot add charges to ${reservation?.status?.toLowerCase()} reservations` : "")}>
                                                <span>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
                                                        onClick={() => setChargeModalOpen(true)}
                                                        disabled={isArchived || isClosedStatus}
                                                        sx={{
                                                            height: 38,
                                                            borderRadius: 2.5,
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            fontSize: '0.825rem',
                                                            px: 2.25,
                                                            whiteSpace: 'nowrap',
                                                            flexShrink: 0,
                                                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                                            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.28)',
                                                            '&:hover': {
                                                                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                                                                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.38)',
                                                                transform: 'translateY(-1px)'
                                                            },
                                                            '&.Mui-disabled': {
                                                                background: '#e2e8f0',
                                                                color: '#94a3b8'
                                                            },
                                                            transition: 'all 0.15s ease'
                                                        }}
                                                    >
                                                        Add Charge
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </>
                                    );
                                })()}
                            </Box>
                        </Box>
                        <TableContainer>
                            <Table size="medium">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: '24%' }}>Posted Date & Time</TableCell>
                                        <TableCell>Description & Category</TableCell>
                                        <TableCell align="right" sx={{ width: '20%' }}>Amount</TableCell>
                                        <TableCell align="right" sx={{ width: '16%' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(folio.items || folio.transactions) && (folio.items || folio.transactions).length > 0 ? (folio.items || folio.transactions).map((txn, idx) => {
                                        const isCredit = txn.type === 'Payment' || txn.type === 'Deposit' || Number(txn.amount || txn.value) < 0;
                                        const isRefund = txn.type === 'Refund' || txn.category === 'Refund';
                                        const isPayment = txn.type === 'Payment' || txn.category === 'Payment';
                                        const isDeposit = txn.type === 'Deposit' || txn.category === 'Deposit';
                                        const dt = formatDateTimeParts(txn.postedAt || txn.createdAt || txn.date);

                                        let badgeColor = { bg: '#fff1f2', text: '#9f1239', border: '#ffe4e6', dot: '#f43f5e' }; // Charge
                                        if (isPayment) {
                                            badgeColor = { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', dot: '#10b981' };
                                        } else if (isDeposit) {
                                            badgeColor = { bg: '#f0fdfa', text: '#0f766e', border: '#99f6e4', dot: '#14b8a6' };
                                        } else if (isRefund) {
                                            badgeColor = { bg: '#fffbeb', text: '#92400e', border: '#fde68a', dot: '#f59e0b' };
                                        }

                                        return (
                                            <TableRow
                                                key={txn.id || idx}
                                                sx={{
                                                    '&:hover': { bgcolor: '#fbfcfd' },
                                                    transition: 'background-color 0.15s ease',
                                                    borderBottom: '1px solid #f1f5f9'
                                                }}
                                            >
                                                <TableCell sx={{ py: 2, px: 3 }}>
                                                    <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600, fontSize: '0.85rem' }}>
                                                        {dt.date}
                                                    </Typography>
                                                    {dt.time && (
                                                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 500, display: 'block', mt: 0.25 }}>
                                                            {dt.time}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ py: 2, px: 2 }}>
                                                    {(() => {
                                                        let desc = txn.description || txn.category || txn.item || 'Standard Entry';
                                                        if (isUae) {
                                                            desc = desc.replace(/gst\s*\([^)]*\)/gi, 'Statutory VAT').replace(/\b(cgst|sgst)\b/gi, 'VAT');
                                                        }
                                                        return (
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="700"
                                                                sx={{
                                                                    color: '#0f172a',
                                                                    textDecoration: txn.isVoided ? 'line-through' : 'none',
                                                                    opacity: txn.isVoided ? 0.5 : 1,
                                                                    fontSize: '0.875rem'
                                                                }}
                                                            >
                                                                {desc}
                                                            </Typography>
                                                        );
                                                    })()}
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <Box component="span" sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 0.6,
                                                            fontSize: '0.7rem',
                                                            fontWeight: 700,
                                                            bgcolor: badgeColor.bg,
                                                            color: badgeColor.text,
                                                            border: `1px solid ${badgeColor.border}`,
                                                            px: 1,
                                                            py: 0.25,
                                                            borderRadius: '9999px',
                                                            letterSpacing: '0.02em',
                                                            textTransform: 'capitalize'
                                                        }}>
                                                            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: badgeColor.dot }} />
                                                            {txn.type || (txn.category === 'Payment' || txn.category === 'Deposit' ? txn.category : 'Charge')}
                                                        </Box>
                                                        {(txn.reference || txn.referenceNumber || txn.paymentMethod) && (
                                                            <Box component="span" sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                color: '#64748b',
                                                                fontSize: '0.72rem',
                                                                fontWeight: 500,
                                                                bgcolor: '#f8fafc',
                                                                border: '1px solid #e2e8f0',
                                                                px: 0.85,
                                                                py: 0.2,
                                                                borderRadius: '6px'
                                                            }}>
                                                                {txn.reference || txn.referenceNumber || txn.paymentMethod}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right" sx={{ py: 2, px: 2 }}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="800"
                                                        sx={{
                                                            color: isCredit ? '#059669' : '#0f172a',
                                                            textDecoration: txn.isVoided ? 'line-through' : 'none',
                                                            opacity: txn.isVoided ? 0.5 : 1,
                                                            fontSize: '0.95rem',
                                                            fontVariantNumeric: 'tabular-nums',
                                                            letterSpacing: '-0.01em'
                                                        }}
                                                    >
                                                        {formatCurrency(Math.abs(Number(txn.amount || txn.value || 0)))}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ py: 2, px: 3 }}>
                                                    {txn.type === 'Charge' && !txn.isVoided && (
                                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                                                            <Tooltip title={isArchived ? "Folio is read-only" : "Edit Charge"}>
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => setEditModalState({ open: true, charge: txn })}
                                                                        disabled={isArchived}
                                                                        sx={{
                                                                            width: 32,
                                                                            height: 32,
                                                                            color: '#2563eb',
                                                                            bgcolor: '#eff6ff',
                                                                            border: '1px solid #dbeafe',
                                                                            borderRadius: '8px',
                                                                            '&:hover': {
                                                                                bgcolor: '#dbeafe',
                                                                                borderColor: '#bfdbfe',
                                                                                transform: 'scale(1.06)'
                                                                            },
                                                                            '&.Mui-disabled': {
                                                                                bgcolor: '#f8fafc',
                                                                                borderColor: '#e2e8f0',
                                                                                color: '#94a3b8'
                                                                            },
                                                                            transition: 'all 0.15s ease'
                                                                        }}
                                                                    >
                                                                        <EditIcon sx={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title={isArchived ? "Folio is read-only" : "Void Charge"}>
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => setVoidModalState({ open: true, charge: txn })}
                                                                        disabled={isArchived}
                                                                        sx={{
                                                                            width: 32,
                                                                            height: 32,
                                                                            color: '#e11d48',
                                                                            bgcolor: '#fff1f2',
                                                                            border: '1px solid #ffe4e6',
                                                                            borderRadius: '8px',
                                                                            '&:hover': {
                                                                                bgcolor: '#ffe4e6',
                                                                                borderColor: '#fecdd3',
                                                                                transform: 'scale(1.06)'
                                                                            },
                                                                            '&.Mui-disabled': {
                                                                                bgcolor: '#f8fafc',
                                                                                borderColor: '#e2e8f0',
                                                                                color: '#94a3b8'
                                                                            },
                                                                            transition: 'all 0.15s ease'
                                                                      }}
                                                                    >
                                                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </Box>
                                                    )}
                                                    {txn.type !== 'Charge' && !txn.isVoided && (
                                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                                                            <Tooltip title="Download PDF Receipt">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => generateReceiptPdf(folio, txn, tenant)}
                                                                    sx={{
                                                                        width: 32,
                                                                        height: 32,
                                                                        color: '#059669',
                                                                        bgcolor: '#ecfdf5',
                                                                        border: '1px solid #a7f3d0',
                                                                        borderRadius: '8px',
                                                                        '&:hover': {
                                                                            bgcolor: '#d1fae5',
                                                                            borderColor: '#6ee7b7',
                                                                            transform: 'scale(1.06)'
                                                                        },
                                                                        transition: 'all 0.15s ease'
                                                                    }}
                                                                >
                                                                    <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    )}
                                                    {txn.isVoided && (
                                                        <Chip label="Voided" size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#fee2e2', color: '#ef4444', fontWeight: 'bold' }} />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                                                <AccountBalanceWalletIcon sx={{ fontSize: 44, color: '#cbd5e1', mb: 1.5 }} />
                                                <Typography variant="body2" fontWeight="600" color="#475569">No financial transactions posted to this folio yet.</Typography>
                                                <Typography variant="caption" color="#94a3b8">Add a charge or collect payment to get started.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>


                    {/* Financial Summary & Balance Due Banner */}
                    {(() => {
                        const totalAmt = parseFloat(folio.totalAmount || 0);
                        const paidAmt = parseFloat(folio.paidAmount || 0);
                        const balance = (totalAmt - paidAmt) || 0;
                        const isCredit = balance < 0;
                        const displayBalance = Math.abs(balance);

                        let gradient = 'linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #4338ca 100%)';
                        let shadow = '0 12px 32px -4px rgba(49, 46, 129, 0.35)';

                        if (isCredit) {
                            gradient = 'linear-gradient(135deg, #064e3b 0%, #047857 60%, #059669 100%)';
                            shadow = '0 12px 32px -4px rgba(4, 120, 87, 0.35)';
                        } else if (balance === 0) {
                            gradient = 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #334155 100%)';
                            shadow = '0 12px 32px -4px rgba(15, 23, 42, 0.35)';
                        } else {
                            gradient = 'linear-gradient(135deg, #881337 0%, #9f1239 50%, #be123c 100%)';
                            shadow = '0 12px 32px -4px rgba(190, 18, 60, 0.35)';
                        }

                        return (
                            <Paper sx={{
                                p: 3.5, borderRadius: 4, elevation: 0, color: 'white',
                                background: gradient, boxShadow: shadow,
                                position: 'relative', overflow: 'hidden'
                            }}>
                                {/* Ambient decorative glowing background shapes */}
                                <Box sx={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)', pointerEvents: 'none' }} />
                                <Box sx={{ position: 'absolute', bottom: -60, left: '30%', width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)', pointerEvents: 'none' }} />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 3 }}>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                            <AccountBalanceWalletIcon sx={{ fontSize: 24, opacity: 0.95 }} />
                                            <Typography variant="h6" fontWeight="800" sx={{ letterSpacing: '-0.01em', fontSize: '1.1rem' }}>
                                                {isCredit ? 'Credit Balance' : (balance === 0 ? 'Settled Folio' : 'Outstanding Balance')}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 420, lineHeight: 1.5, fontSize: '0.825rem' }}>
                                            {isCredit
                                                ? 'Guest has an unused credit balance available on this folio ledger.'
                                                : (balance === 0
                                                    ? 'All charges have been fully settled. Folio balance is zero.'
                                                    : 'Total net balance due including all room tariffs, posted itemized charges, and taxes.')}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                                            <Box sx={{
                                                bgcolor: 'rgba(255, 255, 255, 0.16)',
                                                backdropFilter: 'blur(10px)',
                                                border: '1px solid rgba(255, 255, 255, 0.22)',
                                                px: 1.5, py: 0.5, borderRadius: 2.5,
                                                fontSize: '0.78rem', fontWeight: 600,
                                                display: 'flex', alignItems: 'center', gap: 0.75
                                            }}>
                                                <span style={{ opacity: 0.85 }}>Total Charges:</span>
                                                <b style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(totalAmt)}</b>
                                            </Box>
                                            <Box sx={{
                                                bgcolor: 'rgba(255, 255, 255, 0.16)',
                                                backdropFilter: 'blur(10px)',
                                                border: '1px solid rgba(255, 255, 255, 0.22)',
                                                px: 1.5, py: 0.5, borderRadius: 2.5,
                                                fontSize: '0.78rem', fontWeight: 600,
                                                display: 'flex', alignItems: 'center', gap: 0.75
                                            }}>
                                                <span style={{ opacity: 0.85 }}>Total Payments:</span>
                                                <b style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(paidAmt)}</b>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, flexShrink: 0 }}>
                                        <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.85, fontWeight: 700, fontSize: '0.72rem', display: 'block', mb: 0.5 }}>
                                            {isCredit ? 'Credit Amount' : 'Net Due'}
                                        </Typography>
                                        <Typography variant="h4" fontWeight="900" sx={{ mb: balance > 0 ? 1.75 : 0, textShadow: '0 2px 12px rgba(0,0,0,0.2)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', fontSize: '2rem' }}>
                                            {formatCurrency(displayBalance)}
                                        </Typography>
                                        {balance > 0 && (
                                            <Tooltip title={isArchived ? "Folio is read-only (Property Archived)" : ""}>
                                                <span>
                                                    <Button
                                                        variant="contained"
                                                        onClick={() => setPaymentModalOpen(true)}
                                                        disabled={isArchived}
                                                        sx={{
                                                            bgcolor: '#ffffff',
                                                            color: '#9f1239',
                                                            fontWeight: 800,
                                                            borderRadius: 2.5,
                                                            px: 3.5,
                                                            py: 1,
                                                            fontSize: '0.875rem',
                                                            whiteSpace: 'nowrap',
                                                            textTransform: 'none',
                                                            boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
                                                            '&:hover': {
                                                                bgcolor: '#ffffff',
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: '0 10px 28px rgba(0,0,0,0.26)'
                                                            },
                                                            '&.Mui-disabled': {
                                                                bgcolor: 'rgba(255, 255, 255, 0.85)',
                                                                color: '#94a3b8'
                                                            },
                                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                                        }}
                                                    >
                                                        Collect Payment
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            </Paper>
                        );
                    })()}

                </Box>
            ) : (
                <Box sx={{ p: 6, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <ReceiptLongIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2, mx: 'auto' }} />
                    <Typography variant="h6" color="text.secondary" fontWeight="bold">No Folio Data Found</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 400, mx: 'auto' }}>
                        The folio for this reservation is currently unavailable or has not been generated yet. Please verify the reservation status.
                    </Typography>
                </Box>
            )}

            <AddFolioChargeModal
                open={chargeModalOpen}
                onClose={() => setChargeModalOpen(false)}
                folioId={reservationId}
                reservationRoomId={folio?.reservationRoomId}
                billingMode={billingMode}
                foliosList={foliosList}
                onSuccess={() => fetchFolioDetails(activeFolioIndex)}
                isUae={isUae}
                tenantId={tenantId}
            />

            <EditFolioChargeModal
                open={editModalState.open}
                onClose={() => setEditModalState({ open: false, charge: null })}
                folioId={folio?.folioId || folio?.id}
                charge={editModalState.charge}
                onSuccess={() => fetchFolioDetails(activeFolioIndex)}
                isUae={isUae}
                tenantId={tenantId}
            />

            <VoidFolioChargeModal
                open={voidModalState.open}
                onClose={() => setVoidModalState({ open: false, charge: null })}
                folioId={folio?.id || folio?.folioId || reservationId}
                charge={voidModalState.charge}
                onSuccess={() => fetchFolioDetails(activeFolioIndex)}
            />

            <AddFolioPaymentModal
                open={paymentModalOpen}
                onClose={() => setPaymentModalOpen(false)}
                folio={folio}
                folioId={folio?.id || folio?.folioId || reservationId}
                folioBalance={(parseFloat(folio?.totalAmount || 0) - parseFloat(folio?.paidAmount || 0)) || 0}
                onSuccess={() => fetchFolioDetails(activeFolioIndex)}
                isUae={isUae}
            />

            <ProcessFolioRefundModal
                open={refundModalOpen}
                onClose={() => setRefundModalOpen(false)}
                folioId={folio?.id || folio?.folioId || reservationId}
                onSuccess={() => fetchFolioDetails(activeFolioIndex)}
            />
        </Drawer>
    );
}
